import React from 'react';

import { SplitBlockAppearance, SplitContentSection } from 'api/cms';
import Box from 'components/Box';
import { Button } from 'components/Button';
import ButtonLink from 'components/Cms/ButtonLink';
import { FilestackImage } from 'components/Image';
import { ConditionalLink } from 'components/Link';
import {
  BodyLarge,
  BodySmall,
  HeadingSmall,
  RichText,
  RichTextHeadline,
} from 'components/Text';
import Video from 'components/Video';
import ShowModalContainer from 'containers/ShowModalContainer';
import { colors, gutterWidth } from 'theme';

import { Blockquote } from './Blockquote';
import { ImageContainer } from './ImageContainer';

type Props = {
  section: SplitContentSection;
  fullWidth: boolean;
  appearance: SplitBlockAppearance;
  index: number;
  blockId: string;
  showTextBox: boolean;
  blockIndex: number;
  pageUpdated: string;
  isDesktop: boolean;
};

export function Content({
  section,
  fullWidth,
  appearance,
  index,
  blockId,
  showTextBox,
  blockIndex,
  pageUpdated,
  isDesktop,
}: Props) {
  const overlapping = appearance === 'OVERLAPPING';

  switch (section.type) {
    case 'TEXT': {
      return (
        <Box
          position="relative"
          px={[gutterWidth / 2, null, gutterWidth]}
          pr={showTextBox ? gutterWidth / 3 : [null, null, 0]}
          py={[
            gutterWidth / 2,
            null,
            ['OVERLAPPING', 'FULL_WIDTH'].includes(appearance)
              ? gutterWidth
              : 0,
          ]}
          height="100%"
        >
          {section.data.label && (
            <HeadingSmall color={colors.battleshipGrey}>
              {section.data.label}
            </HeadingSmall>
          )}
          {section.data.headline && (
            <Box
              display="block"
              mb={24}
              color={colors.greyishBrown}
              textDecoration="none"
            >
              <RichTextHeadline>{section.data.headline}</RichTextHeadline>
            </Box>
          )}
          <RichText
            analyticsData={{
              eventName: 'CMS Block',
              eventProperties: {
                block_position: blockIndex,
                block_type: 'Split Content',
              },
            }}
            dangerouslySetInnerHTML={{
              __html: section.data.html,
            }}
            styleAs="body"
            tag="div"
          />
          {section.data.button && (
            <Box classes={!isDesktop ? ['flex', 'justify-center'] : []}>
              <ButtonLink
                sectionButton={section.data.button}
                eventProperties={{
                  block_position: blockIndex,
                  block_type: 'Split Content',
                  cta_type: 'Button',
                  button_text: section.data.button.text,
                  page_updated: pageUpdated,
                }}
                blockId={blockId}
                index={index}
              />
            </Box>
          )}
        </Box>
      );
    }

    case 'BLOCKQUOTE':
      return (
        <Blockquote
          component="blockquote"
          position="relative"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          height="100%"
          m={0}
          p={[gutterWidth / 2, null, gutterWidth]}
        >
          <BodyLarge
            analyticsData={{
              eventName: 'CMS Block',
              eventProperties: {
                block_position: blockIndex,
                block_type: 'Split Content',
              },
            }}
            dangerouslySetInnerHTML={{
              __html: section.data.quote,
            }}
          />
          {section.data.cite && <BodySmall>{section.data.cite}</BodySmall>}
        </Blockquote>
      );

    case 'VIDEO':
      return (
        <ShowModalContainer
          render={(showModal) => (
            <Box
              component={Button}
              position="relative"
              width="100%"
              height="100%"
              onClick={() => {
                showModal('MEDIA_GALLERY', {
                  items: [
                    {
                      type: 'VIDEO',
                      videoUrl: section.data.url,
                    },
                  ],
                  itemIndex: 0,
                });
              }}
              aria-label={getText('Open video')}
            >
              <Video
                minHeight={['50vw', null, '25vw', (540 * 9) / 16]}
                url={section.data.url}
                prefix="split-block-video-"
                fill
                noPlayer
                hideButton={fullWidth}
              />
            </Box>
          )}
        />
      );

    case 'IMAGE': {
      const format = !['image/gif', 'image/jpeg', 'image/png'].includes(
        section.data.image.mimetype
      )
        ? 'jpg'
        : undefined;
      return (
        <ConditionalLink
          link={section.data.url}
          data-qa-id="split-content-image-url"
        >
          <ImageContainer
            handle={section.data.image && section.data.image.handle}
            mimetype={section.data.image && section.data.image.mimetype}
            format={format}
            fixedWidth={section.data.size === 'FIXED_WIDTH'}
            fullWidth={fullWidth}
            overlapping={overlapping}
            component="div"
          >
            <FilestackImage
              data={section.data.image}
              alt={
                (section.data.image && section.data.image.altText) ||
                (section.data.image && section.data.image.filename)
              }
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line no-nested-ternary
              width={fullWidth ? 1440 : overlapping ? 696 : 552}
              format={format}
            />
          </ImageContainer>
        </ConditionalLink>
      );
    }

    default:
      return null;
  }
}
